import { motion } from "framer-motion";
import { 
  Settings, 
  CheckCircle, 
  Bell, 
  BarChart3, 
  MessageCircle, 
  Calendar 
} from "lucide-react";

const Features = () => {
  const features = [
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: "Instant vs Batched Delivery",
      description: "Sera allows you to select which apps can break through your batching schedule to be delivered immediately.",
      photo: "/app-screens/features/instant_batched.svg",
      photoAlt: "Sera schedule screen preview"
    },
    {
      icon: <Bell className="w-8 h-8" />,
      title: "Custom Batch Scheduled",
      description: "Schedule your batches to be delivered at times you choose that best fit your productivity and focus goals.",
      photo: "/app-screens/app_schedule.svg",
      photoAlt: "Sera schedule screen preview"
    },
    {
      icon: <MessageCircle className="w-8 h-8" />,
      title: "Custom Override Rules",
      description: "Create custom override rules to allow certain notifications to break through your batching schedule even if that app is normally scheduled to be batched.",
      photo: "/app-screens/features/rules2.png",
      photoAlt: "Sera schedule screen preview"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Notification Insights",
      description: "View detailed insights into your notification patterns and optimize your focus time.",
      photo: "/app-screens/features/insights.png",
      photoAlt: "Sera inbox screen preview"
    },
    {
      icon: <Calendar className="w-8 h-8" />,
      title: "Pause & Resume Batching",
      description: "Pause and resume batching at any time to fit your changing needs and productivity goals.",
      photo: "/app-screens/features/pause.svg",
      photoAlt: "Sera inbox screen preview"
    },
    {
      icon: <Settings className="w-8 h-8" />,
      title: "Light & Dark Mode",
      description: "Sera is available in light and dark mode to suit your preference.",
      photo: "/app-screens/features/theme.svg",
      photoAlt: "Sera light and dark mode preview"
    }
  ];

  return (
    <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-800/30">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Discover Sera Features
          </h2>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto">
            Explore Sera's powerful features designed to help you batch, focus, and manage notifications effortlessly.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group cursor-pointer relative"
              whileHover={{ 
                y: -8,
                scale: 1.02,
                transition: { duration: 0.3, ease: "easeOut" }
              }}
              whileTap={{ scale: 0.98 }}
            >
              {/* Animated background glow effect - contained within the card */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-teal-400/20 to-blue-500/20 rounded-2xl opacity-0 group-hover:opacity-100 blur-xl -z-10"
                transition={{ duration: 0.3 }}
              />
              
              {/* Main content container */}
              <div className="relative z-10 h-full p-8 rounded-2xl bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 group-hover:border-teal-400/30 transition-all duration-300 flex flex-col">
                {/* Icon container with enhanced animations */}
                <motion.div 
                  className="w-12 h-12 bg-gradient-to-br from-teal-400/10 to-blue-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:from-teal-400/20 group-hover:to-blue-500/20 transition-all duration-300 flex-shrink-0"
                  whileHover={{
                    scale: 1.1,
                    rotate: [0, -5, 5, -5, 0],
                    transition: { duration: 0.6, ease: "easeInOut" }
                  }}
                >
                  <motion.div 
                    className="text-teal-400 group-hover:text-teal-300 transition-colors duration-300"
                    whileHover={{
                      scale: 1.2,
                      transition: { duration: 0.2 }
                    }}
                  >
                    {feature.icon}
                  </motion.div>
                </motion.div>
                
                {/* Content area that grows to fill available space */}
                <div className="flex flex-col flex-grow">
                  {/* Title with enhanced hover effects */}
                  <motion.h3 
                    className="text-xl font-semibold mb-4 group-hover:text-teal-400 transition-colors duration-300 flex-shrink-0"
                    whileHover={{
                      scale: 1.05,
                      transition: { duration: 0.2 }
                    }}
                  >
                    {feature.title}
                  </motion.h3>
                  
                  {/* Description with subtle animation - grows to fill remaining space */}
                  <motion.p 
                    className="text-slate-400 mb-6 group-hover:text-slate-300 transition-colors duration-300 flex-grow"
                    whileHover={{
                      y: -2,
                      transition: { duration: 0.2 }
                    }}
                  >
                    {feature.description}
                  </motion.p>
                  
                  {/* Feature preview with enhanced styling */}
                  <motion.div 
                    className="w-full h-48 bg-gradient-to-br from-slate-700/50 to-slate-600/50 rounded-lg border border-slate-600/50 group-hover:border-teal-400/30 group-hover:from-slate-600/50 group-hover:to-slate-500/50 transition-all duration-300 flex-shrink-0 overflow-hidden p-3"
                    whileHover={{
                      scale: 1.02,
                      transition: { duration: 0.2 }
                    }}
                  >
                    <img
                      src={feature.photo}
                      alt={feature.photoAlt}
                      className="w-full h-full object-cover bg-slate-900/70 rounded-md transition-transform duration-300 group-hover:scale-105"
                    />
                  </motion.div>
                </div>
                
                {/* Decorative elements that appear on hover */}
                <motion.div
                  className="absolute top-4 right-4 w-2 h-2 bg-teal-400 rounded-full opacity-0 group-hover:opacity-100"
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0, 1, 0]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: 0.5
                  }}
                />
                <motion.div
                  className="absolute bottom-4 left-4 w-1 h-1 bg-blue-400 rounded-full opacity-0 group-hover:opacity-100"
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0, 1, 0]
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: 1
                  }}
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
